import React, { useMemo, useState } from 'react';
import { Box, ChevronDownIcon, ChevronUpIcon, FlatTextInput, LargeCloseIcon } from '@common-fe/common-fe';

import theme from '@/styles/theme';

import { INLINE_CLEAR_ICON_MAX_COLUMNS } from '../PayoutOrder.constants';

const DEFAULT_WIDTH = '320px';
const INPUT_PADDING_RIGHT = '42px';
const INPUT_PADDING_RIGHT_WITH_CLEAR = '64px';

interface Props {
  children: string;
  isActive: boolean;
  isError: boolean;
  isDisabled?: boolean;
  setIsShown: (value: boolean) => void;
  placeholder?: string;
  width?: string;
  leftIcon?: React.ReactNode;
  onClear?: () => void;
  columnsCount?: number;
}

const DropdownTriggerInput: React.FC<Props> = ({
  isActive,
  children,
  isError,
  isDisabled,
  setIsShown,
  placeholder,
  width,
  leftIcon,
  onClear,
  columnsCount,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const showClearIcon = !!onClear && !!children && !isDisabled && isHovered;

  /**
   * When there are few columns there is enough horizontal room to show the
   * clear icon next to the expand/collapse chevron. When there are many
   * columns space is tight, so the clear icon replaces the chevron on hover.
   */
  const showClearInline = showClearIcon && (columnsCount ?? 0) <= INLINE_CLEAR_ICON_MAX_COLUMNS;

  const clearIcon = (
    <LargeCloseIcon
      color="iconPrimary"
      style={{ cursor: 'pointer' }}
      data-testid="payout-order_plan-dropdown_clear"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onClear?.();
      }}
    />
  );

  const chevronIcon = isActive ? (
    <ChevronUpIcon color="iconPrimary" />
  ) : (
    <ChevronDownIcon color="iconPrimary" />
  );

  const rightIcon = useMemo(() => {
    if (showClearInline) {
      return (
        <Box direction="row" align="center" gap="spacing4" style={{ paddingRight: theme.spacings.xxs }}>
          {clearIcon}
          {chevronIcon}
        </Box>
      );
    }
    if (showClearIcon) {
      return <Box style={{ paddingRight: theme.spacings.xxs }}>{clearIcon}</Box>;
    }
    return <Box style={{ paddingRight: theme.spacings.xxs }}>{chevronIcon}</Box>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, showClearIcon, showClearInline]);

  const handleIconClick = () => {
    // Inline clear handles its own click via onClick + stopPropagation.
    if (showClearIcon && !showClearInline && onClear) {
      onClear();
      return;
    }
    setIsShown(!isActive);
  };

  return (
    <Box
      data-testid="payout-order_plan-dropdown_trigger"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FlatTextInput
        value={children}
        style={{
          width: width || DEFAULT_WIDTH,
          paddingRight: showClearInline ? INPUT_PADDING_RIGHT_WITH_CLEAR : INPUT_PADDING_RIGHT,
          cursor: 'pointer',
        }}
        placeholder={placeholder || 'Select plan'}
        rightIcon={rightIcon}
        isActive={isActive}
        leftIcon={leftIcon && children ? leftIcon : undefined}
        disabled={isDisabled}
        onIconClick={handleIconClick}
        ellipsisMode
        isError={isError}
      />
    </Box>
  );
};

export default DropdownTriggerInput;
